import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {OrgService} from '../services/org.service';
import {APIURL} from "../services/url.config";
import {AlertService} from "../services/alert.service";
import { SecureTokenService } from '../services/secure-token.service';

declare var $: any;

@Component({
  selector: 'tenant-mappings-root',
  templateUrl: './mappings-tenant-root.component.html',
  providers: [OrgService, Location]
})

/**
 * Component for the org/company settings page.
 */
export class TenantMappingsRootComponent implements OnInit, OnDestroy {
  // Lists of all mappings & mappings to be displayed
  fullMappings: any;
  tenantMappings: any;

  // Lists for dropdown options
  tenantList = [];
  actionList = ['Update', 'Delete'];

  // TODO: Generate list of application types, default to engage, but expand later on
  application: any = 'engage';
  applicationList = ['engage'];

  // Body/URL variables for API requests
  tenant: any;
  oid: any;
  url: any;
  currAction: any = 'Update';
  hiddenAction: any = 'Delete';
  username: String = sessionStorage.getItem('username');

  @ViewChild('confirmDeleteModal', { static: false }) confirmDeleteModal: ElementRef;


  // Form initialization for Update & Delete
  updateForm = new UntypedFormGroup({
    oid: new UntypedFormControl(),
    tenant: new UntypedFormControl()
  });

  deleteForm = new UntypedFormGroup({
    oid: new UntypedFormControl()
  });

  constructor(private _service: OrgService,
              private _router: Router,
              private _route: ActivatedRoute,
              private http: HttpClient,
              private _el: ElementRef,
              private alertService: AlertService) {
  }

  /**
   * Get the current org settings object and, if anything is unset, add default values.
   *
   * @async
   * @func ngOnInit
   */
  ngOnInit() {
    this.loadMappings();
  }

  /**
   * Disposes of the subscriber object.
   *
   * @async
   * @func ngOnDestroy
   */
  ngOnDestroy() {
    // pass
  }

  // Load all currently existing tenant mappings across all systems
  private async loadMappings() {
    // Implemented onInit
    let myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Bearer " + SecureTokenService.getAccessToken());

    this.oid = null;
    this.tenant = null;

    let requestOptions: RequestInit = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    };

    // Retrieve all mappings
    let mappingSet = fetch(APIURL + 'api/uaa/tenant_mapping/all_mappings', requestOptions)
        .then(response => response.json())
        .then(result => {
          // Set values of full and display arrays
          this.fullMappings = result;
          this.tenantMappings = result;
          return result;
        }).then(next => {
          this.clearForms();
        })
        .catch(error => {
          console.log('Mapping Tenant Retrieval Failed.');
          console.log('error', error);
        });
    await mappingSet;
  }

  // Submit form values to Create, Update, or Delete mapping
  onSubmit(formValues) {
    // set up the api call
    const headers = new HttpHeaders()
        .set('Authorization', 'Bearer ' + SecureTokenService.getAccessToken())
        .set('cache-control', 'no-cache, private')
        .set('Content-Type', 'application/json');

    // Angular 2 workaround - form values aren't always registered unless user types in input box, so this catches those values
    if(formValues.oid !== null) {
      if(formValues.oid.trim() !== '') {
        this.oid = formValues.oid;
      } else {
        this.oid = '';
      }
    } else {
      formValues.oid = this.oid;
    }

    if (this.currAction === 'Update') {
      // Angular 2 workaround - form values aren't always registered unless user types in input box, so this catches those values
      if(formValues.tenant !== null) {
        if(formValues.oid.trim() !== '') {
          this.tenant = formValues.tenant;
        } else {
          this.tenant = '';
        }
      } else {
        formValues.tenant = this.tenant;
      }

      const body = {
        oid: this.oid,
        tenant: this.tenant,
        application: this.application
      };

      this.url = APIURL + 'api/uaa/tenant_mapping';

      // Error Check - Ensures proper error message is shown to user if oid, tenant, or both are invalid
      if(this.checkExistingOid(this.oid)) {
        if(this.tenant.trim() !== '') {
          this.updateMapping(body, headers);
        } else {
          this.clearForms();
          this.alertService.showAlert("Invalid Tenant provided.\nPlease ensure the Tenant given meets the following:\n " +
              "- Not empty\n - Does not contain only spaces\n - Under 100 characters");
        }
      } else if(this.tenant !== null && this.tenant.trim() !== '') {
        this.clearForms();
        this.alertService.showAlert("Invalid OID provided.\nPlease ensure the OID given matches that of an existing mapping.");
      } else {
        this.clearForms();
        this.alertService.showAlert("Invalid OID and Tenant provided.\nPlease ensure the OID given matches that of an existing mapping, and the Tenant given meets the following:\n " +
            "- Not empty\n - Does not contain only spaces\n - Under 100 characters");
      }

    } else if (this.currAction == 'Delete') {
      this.url = APIURL + 'api/uaa/tenant_mapping?oid=' + this.oid;

      if(this.checkExistingOid(this.oid)) {
        this.confirmDeleteModal.nativeElement.classList.toggle("show");
      //   if(confirm("Are you sure you want to delete this mapping with OID: " + this.oid + "?")) {
      //     this.deleteMapping(headers);
      //   }
      } else {
        this.clearForms();
        this.alertService.showAlert("Invalid OID provided.\nPlease ensure the OID given matches that of an existing mapping.");
      }

    } else {
      this.alertService.showAlert('Invalid Parameters Provided.');
      return;
    }
  }

  // Update mapping API request
  private updateMapping(body, headers) {
    return this.http
        .put(this.url, body, { headers: headers})
        .subscribe(
            success => {
              console.log('UPDATE successful');
              this.loadMappings();
            }, response => {
              console.log('UPDATE call in error', response);
              this.alertService.showAlert("Failed to update mapping.");
            }, () => {
              console.log('UPDATE end.');
            }
        );
  }

  // Delete mapping API request
  private deleteMapping(headers) {
    return this.http
        .delete(this.url, { headers: headers})
        .subscribe(
            success => {
              console.log('DELETE successful');
              this.loadMappings();
            }, response => {
              console.log('DELETE call in error', response);
              this.alertService.showAlert("Failed to delete mapping.");
            }, () => {
              console.log('DELETE end.');
            }
        );
  }

  // Search existing mappings list by Tenant or Oid, including partials
  searchTenantOrOid() {
    let input = (<HTMLInputElement>document.getElementById('searchOidOrTenant'));

    let filteredList = [];

    if(input.value !== null && input.value !== '' && input.value !== undefined) {
      for(let i = 0; i < this.fullMappings.length; i++){
        if(this.fullMappings[i].tenant.includes(input.value) || this.fullMappings[i].oid.includes(input.value)) {
          filteredList.push(this.fullMappings[i]);
        }
      }
      this.tenantMappings = filteredList;
    } else {
      this.tenantMappings = this.fullMappings;
    }
  }

  // Alert user if input value reaches max of 100 characters
  checkInputLength(elementId) {
    let input = (<HTMLInputElement>document.getElementById(elementId));

    if(input.value !== null && input.value !== undefined) {
      if(input.value.length >= 100) {
        this.alertService.showAlert('Input value reached max length of 100 characters.');
      }
    }
  }

  // Update action to display proper form & execute proper API request
  changeAction() {
    this.clearForms();
    if(this.currAction == 'Delete') {
      this.currAction = 'Update';
      this.hiddenAction = 'Delete';
      $('#deleteMappingForm').hide();
      $('#updateMappingForm').show();
    } else {
      this.currAction = 'Delete';
      this.hiddenAction = 'Update';
      $('#updateMappingForm').hide();
      $('#deleteMappingForm').show();
    }
  }

  // Populate the form values with those of the mapping clicked
  fillFormWithExisting(input) {
    this.oid = null;
    this.tenant = null;

    if(this.currAction === 'Update') {
      this.oid = input.oid;
      this.tenant = input.tenant;

      if (document.getElementById('updateTenant') !== null) {
        const formval = (<HTMLInputElement>document.getElementById('updateTenant'));
        formval.value = input.tenant;
        (<HTMLInputElement>document.getElementById('updateTenant')).setAttribute('value', input.tenant);
      }
      if (document.getElementById('updateOid') !== null) {
        const formval = (<HTMLInputElement>document.getElementById('updateOid'));
        formval.value = input.oid;
        (<HTMLInputElement>document.getElementById('updateOid')).setAttribute('value', input.oid);
      }
    }

    if(this.currAction === 'Delete') {
      this.oid = input.oid;

      if (document.getElementById('deleteOid') !== null) {
        const formval = (<HTMLInputElement>document.getElementById('deleteOid'));
        formval.value = input.oid;
        (<HTMLInputElement>document.getElementById('deleteOid')).setAttribute('value', input.oid);
      }
    }
  }

  //Check delete OID
  checkExistingOid(input) {
    if(input !== null && input.trim() !== '') {
      for(let i = 0; i < this.fullMappings.length; i++){
        if(this.fullMappings[i].oid == input) {
          return true;
        }
      }
    }
    return false;
  }

  // Clear the Delete and Update forms so no data is mistakenly re-submitted
  clearForms() {
    this.oid = null;
    this.tenant = null;
    this.deleteForm.reset();
    this.updateForm.reset();
  }

  // Reduces OID value to traversable ~ extension in routing parameters
  reduceOid(input) {
    return input.toString().replace('2.16.840.1.113883.3.21', '~');
  }

  confirmDelete() {
    const headers = new HttpHeaders()
        .set('Authorization', 'Bearer ' + SecureTokenService.getAccessToken())
        .set('cache-control', 'no-cache, private')
        .set('Content-Type', 'application/json');

    this.confirmDeleteModal.nativeElement.classList.toggle("show");
    this.deleteMapping(headers);
  }

  declineDelete() {
    this.confirmDeleteModal.nativeElement.classList.toggle("show");
  }

  enableDisableMapping(elementId) {
    let input = (<HTMLInputElement>document.getElementById(elementId));
    if(input.value !== null && input.value !== undefined && input.value.length > 0)
      return false;
    else
      return true;
  }
}
